import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import {
  PageHeader,
  Button,
  Menu,
  Badge,
  Plus,
  DotsVertical,
  Edit01,
  Copy,
  XCircle,
  ChevronDown,
  GraduationHat01,
  LinkExternal01,
  Code02,
} from "@opendate-app/setlist";
import { toastError, toastSuccess } from "../../shared/toastHelper";
import ClassCapacityMeter from "../ClassesShared/ClassCapacityMeter";
import ClassStatusBadge, { upcomingOpensAt } from "../ClassesShared/ClassStatusBadge";
import ClassFormatBadge from "../ClassesShared/ClassFormatBadge";
import AvatarStack from "../ClassesShared/AvatarStack";
import EmptyStateCard from "../ClassesShared/EmptyStateCard";
import IndexToolbar, { IndexOverlays, IndexTable } from "../ClassesShared/IndexToolbar";
import useClassesIndexTable, { ClassesIndexTableContext } from "../ClassesShared/useClassesIndexTable";
import { formatDateRange, isClassEditable } from "../ClassesShared/classLabels";
import CancelClassModal from "./CancelClassModal";
import ClassesEmbedModal, { embedFilterShape } from "./ClassesEmbedModal";
import cancelClassErrorMessage from "./cancelClassErrorMessage";
import { buildClassInstanceFilterGroups } from "./constants/classInstanceFiltersConfig";
import {
  CLASS_INSTANCE_COLUMNS,
  SORT_OPTIONS,
  SORTABLE_COLUMNS,
  tableSortFor,
} from "./constants/classInstanceColumnsConfig";
import "../ClassesShared/classesShared.css";

const listed = (values) => (Array.isArray(values) && values.length > 0 ? values : null);

const classesQuery = (filters) => {
  const q = {
    status_in: listed(filters.status),
    class_template_id_in: listed(filters.template),
    venue_ownership_id_in: listed(filters.venue),
    class_sessions_instructors_id_in: listed(filters.instructor),
    with_waitlist: filters.waitlist?.includes("has_waitlist") || null,
    created_at_gteq: filters.created_at?.start || null,
    created_at_lteq: filters.created_at?.end ? `${filters.created_at.end}T23:59:59` : null,
  };
  return Object.fromEntries(Object.entries(q).filter(([, value]) => value != null));
};

const optionShape = PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), label: PropTypes.string });

function ClassesIndexApp(props) {
  const {
    indexPath,
    newPath,
    csrfToken,
    canManage,
    teamId,
    statusOptions,
    templateOptions,
    venueOptions,
    instructorOptions,
    templatesPath,
    newTemplatePath,
    catalogLinks = [],
    embedFilters = [],
  } = props;

  const [cancelTarget, setCancelTarget] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [embedModalKey, setEmbedModalKey] = useState(0);

  const filterGroups = useMemo(
    () => buildClassInstanceFilterGroups(statusOptions, templateOptions, venueOptions, instructorOptions),
    [statusOptions, templateOptions, venueOptions, instructorOptions]
  );

  const table = useClassesIndexTable({
    indexPath,
    csrfToken,
    teamId,
    storageKey: "classInstance",
    dataKey: "classInstances",
    noun: "classes",
    kind: "class",
    columns: CLASS_INSTANCE_COLUMNS,
    sortOptions: SORT_OPTIONS,
    filterGroups,
    buildQuery: classesQuery,
    buildParams: (filters) => ({ class_date_start: filters.class_date?.start, class_date_end: filters.class_date?.end }),
  });
  const { load } = table;

  const onDuplicate = useCallback(
    async (c) => {
      try {
        const resp = await axios.post(`${indexPath}/${c.id}/duplicate`, {}, { headers: { "X-CSRF-Token": csrfToken } });
        toastSuccess("Class duplicated");
        window.location.href = `${indexPath}/${resp.data.id}/edit`;
      } catch (e) {
        toastError("Could not duplicate class.");
      }
    },
    [indexPath, csrfToken]
  );

  const onCancel = useCallback((c) => setCancelTarget(c), []);

  const submitCancel = useCallback(
    async (reason) => {
      if (!cancelTarget) return;
      setCanceling(true);
      try {
        await axios.post(`${indexPath}/${cancelTarget.id}/cancel`, { cancellation_reason: reason }, { headers: { "X-CSRF-Token": csrfToken } });
        toastSuccess("Class canceled");
        setCancelTarget(null);
        load();
      } catch (e) {
        toastError(cancelClassErrorMessage(e));
      } finally {
        setCanceling(false);
      }
    },
    [cancelTarget, indexPath, csrfToken, load]
  );

  const columns = useMemo(() => {
    const cols = table.visibleColumns.map((col) => {
      switch (col.id) {
        case "name":
          return {
            key: "name",
            header: "Class",
            primary: true,
            sortable: true,
            render: (c) => (
              <a href={`${indexPath}/${c.id}`} className="text-reset text-decoration-none">
                {c.name}
              </a>
            ),
          };
        case "template_name":
          return { key: "template_name", header: "Template", render: (c) => c.template_name || "—" };
        case "date_range":
          return { key: "date_range", header: "Date range", sortable: true, render: (c) => formatDateRange(c.starts_on, c.ends_on, c.time_zone) };
        case "format":
          return {
            key: "format",
            header: "Format",
            render: (c) => <ClassFormatBadge format={c.format} />,
          };
        case "capacity":
          return {
            key: "capacity",
            header: "Capacity",
            render: (c) => <ClassCapacityMeter taken={c.spots_taken || 0} capacity={c.capacity} percent={c.capacity_percent} level={c.capacity_level} status={c.status} />,
          };
        case "instructors":
          return {
            key: "instructors",
            header: "Instructors",
            render: (c) => <AvatarStack people={(c.instructors || []).map((instructor) => ({ id: instructor.id, name: instructor.name, src: instructor.photo_url }))} />,
          };
        case "status":
          return {
            key: "status",
            header: "Status",
            render: (c) => (c.status === "published" && c.waitlist_count > 0
              ? <Badge color="warning" size="sm" dot>{`Waitlist (${c.waitlist_count})`}</Badge>
              : <ClassStatusBadge status={c.status} opensAt={upcomingOpensAt(c)} timeZone={c.time_zone} />),
          };
        default:
          return { key: col.id, header: col.label, render: () => "—" };
      }
    });

    if (canManage) {
      cols.push({
        key: "actions",
        header: <span className="sr-only">Actions</span>,
        align: "right",
        render: (c) => (
          <Menu
            placement="bottom-end"
            trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${c.name}`} />}
            items={[
              ...(isClassEditable(c.status)
                ? [{ label: "Edit Class", icon: Edit01, onClick: () => { window.location.href = `${indexPath}/${c.id}/edit`; } }]
                : []),
              { label: "Duplicate", icon: Copy, onClick: () => onDuplicate(c) },
              ...(c.may_cancel
                ? [{ label: "Cancel", icon: XCircle, onClick: () => onCancel(c) }]
                : []),
            ]}
          />
        ),
      });
    }
    return cols;
  }, [table.visibleColumns, indexPath, canManage, onDuplicate, onCancel]);

  const catalogEmpty = table.loaded && !table.loading && !table.error && table.totalEntries === 0 && !table.filtering;
  const go = (path) => () => { window.location.href = path; };
  const hasTemplates = (templateOptions || []).length > 0;

  const shareItems = [
    ...catalogLinks.map((link) => ({
      label: catalogLinks.length === 1 ? "Open Public Catalog" : `Open ${link.label} Catalog`,
      icon: LinkExternal01,
      onClick: () => { window.open(link.href, "_blank", "noopener"); },
    })),
    { label: "Get Embed Code", icon: Code02, onClick: () => { setEmbedModalKey((key) => key + 1); setEmbedModalOpen(true); } },
  ];

  const emptyActions = !canManage ? {} : hasTemplates
    ? {
      primaryAction: { label: "Schedule Class", icon: Plus, onClick: go(newPath) },
      secondaryAction: templatesPath ? { label: "View Templates", onClick: go(templatesPath) } : undefined,
    }
    : {
      primaryAction: { label: "Create Your First Template", icon: Plus, onClick: go(newTemplatePath) },
      secondaryAction: { label: "Schedule a One-Off Class", onClick: go(newPath) },
    };

  return (
    <ClassesIndexTableContext.Provider value={table}>
      <div className="od-themed p-4">
        <PageHeader
          title={<span role="heading" aria-level={1}>Classes</span>}
          supportingText="Scheduled classes ready for registrants to book."
          actions={
            <div className="d-flex align-items-center flex-wrap classes-gap-8">
              {catalogLinks.length > 0 && (
                <Menu
                  placement="bottom-end"
                  trigger={<Button variant="secondary" icon={LinkExternal01} trailingIcon={ChevronDown}>Public Catalog</Button>}
                  items={shareItems}
                />
              )}
              {canManage && (
                <Button as="a" variant="primary" icon={Plus} href={newPath}>Schedule Class</Button>
              )}
            </div>
          }
        />

        {catalogEmpty ? (
          <div className="mt-4">
            <EmptyStateCard
              icon={GraduationHat01}
              title={hasTemplates ? "Schedule your first class" : "Build your class catalog"}
              description={hasTemplates
                ? "Pick a template, set the dates and publish. Each class gets its own public page that people can register from."
                : "Templates are reusable definitions of a class: name, description, format and default pricing. Create a template once, then schedule as many runs from it as you need."}
              {...emptyActions}
            />
          </div>
        ) : (
          <>
            <IndexToolbar />
            <IndexTable
              columns={columns}
              stickyFirstColumn
              stickyLastColumn={canManage}
              sort={tableSortFor(table.sortBy)}
              onSortChange={(key, direction) => table.setSortBy(`${SORTABLE_COLUMNS[key]} ${direction}`)}
              empty={table.filtering ? "No classes match your search or filters." : "No classes scheduled yet."}
            />
          </>
        )}

        <IndexOverlays />

        {cancelTarget && (
          <CancelClassModal
            classLabel={cancelTarget.name}
            registrationCount={cancelTarget.registered_count ?? 0}
            requireReason={cancelTarget.status === "published"}
            working={canceling}
            onClose={() => setCancelTarget(null)}
            onConfirm={submitCancel}
          />
        )}

        <ClassesEmbedModal
          key={embedModalKey}
          open={embedModalOpen}
          onClose={() => setEmbedModalOpen(false)}
          venues={catalogLinks}
          filters={embedFilters}
        />

        <ToastContainer icon={false} />
      </div>
    </ClassesIndexTableContext.Provider>
  );
}

ClassesIndexApp.propTypes = {
  indexPath: PropTypes.string.isRequired,
  newPath: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  canManage: PropTypes.bool,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  statusOptions: PropTypes.arrayOf(optionShape),
  templateOptions: PropTypes.arrayOf(optionShape),
  venueOptions: PropTypes.arrayOf(optionShape),
  instructorOptions: PropTypes.arrayOf(optionShape),
  templatesPath: PropTypes.string,
  newTemplatePath: PropTypes.string,
  catalogLinks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      href: PropTypes.string,
      embedUrl: PropTypes.string,
    })
  ),
  embedFilters: PropTypes.arrayOf(embedFilterShape),
};

export default ClassesIndexApp;
